import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Eye, EyeOff, KeyRound, Plug, RefreshCw, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { fmtNum, timeAgo } from "@/components/investor/format";

const SAMPLE_VENTA = `POST /api/rubipos/ventas
Header: X-API-Key: <tu_api_key>

{
  "ventas": [
    {
      "sucursal_id": "PZR-01",          // ID de la sucursal en RubiPOS (obligatorio)
      "sucursal_nombre": "Poza Rica",   // opcional, se usa si la sucursal es nueva
      "folio": "T-000123",              // obligatorio · único por sucursal (idempotente)
      "fecha": "2026-06-15T14:32:00",   // ISO 8601 (hora local de la sucursal)
      "total": 1299.00,                 // obligatorio · total cobrado
      "subtotal": 1120.00, "impuestos": 179.00, "descuento": 0,
      "costo": 640.00,                  // opcional · costo de mercancía (si falta, se usa el % de margen)
      "forma_pago": "tarjeta", "vendedor": "Ana",
      "cancelada": false,               // true = se excluye de las métricas
      "items": [{ "sku": "RB-100", "descripcion": "Perfume Dama 100ml", "cantidad": 1, "precio": 1299.00 }]
    }
  ]
}`;
const SAMPLE_GASTO = `POST /api/rubipos/gastos
Header: X-API-Key: <tu_api_key>

{ "gastos": [ { "sucursal_id": "PZR-01", "folio": "G-2026-06-001", "fecha": "2026-06-05T09:00:00",
                "concepto": "Renta local", "categoria": "renta", "monto": 62700.00 } ] }`;
const SAMPLE_TRASPASO = `POST /api/rubipos/traspasos          // compras de insumos / traspasos hacia la sucursal
Header: X-API-Key: <tu_api_key>

{ "traspasos": [ {
    "external_id": "TR-1001", "folio": "TR-1001",           // external_id o folio obligatorio (idempotente)
    "sucursal_id": "9e5212b5-…", "sucursal_codigo": "MAD", "sucursal_nombre": "Rubi Perfumeria Madero",
    "estado": "recibido",                                   // enviado (en tránsito) | recibido (cuenta como compra) | cancelado
    "lista_precio": "franquicia", "origen_id": "c1c15990-…", "origen_nombre": "Almacén Central",
    "fecha": "2026-06-03T10:00:00", "recibido_por": "Vero", "incidencia": null,
    "productos": [ { "sku": "P7C0275", "nombre": "Stronger 30ml", "cantidad_enviada": 20, "cantidad_recibida": 20, "precio": 85.00, "importe": 1700.00 } ],
    "subtotal": 1700.00                                     // se usa directamente como costo de mercancía
} ] }`;
const SAMPLE_CORTE = `POST /api/rubipos/cortes             // cortes de caja (cierre de turno)
Header: X-API-Key: <tu_api_key>

{ "cortes": [ {
    "external_id": "CT-501", "folio": "CT-501",             // external_id o folio obligatorio (idempotente)
    "sucursal_id": "9e5212b5-…", "caja": "Caja 01", "cajero": "Verónica S.",
    "fecha": "2026-06-11T21:05:00",                          // cierre (ISO 8601) · "fecha_apertura" opcional
    "ventas_total": 7094.00, "tickets": 19,
    "efectivo_esperado": 3200.00, "efectivo_contado": 3150.00, "diferencia": -50.00,   // diferencia opcional (se calcula)
    "formas_pago": { "efectivo": 3200.00, "tarjeta": 3894.00 }, "retiros": 0, "estado": "cerrado"
} ] }`;

const SECTIONS = [{ key: "integraciones", label: "Integraciones", icon: <Plug size={13} /> }];

export default function IntegrationsPanel() {
  const [section, setSection] = useState("integraciones");
  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="settings-panel">
      <aside className="lg:col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Configuración</div>
        <nav className="flex lg:flex-col gap-1">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)} className={`flex items-center gap-2 text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${section === s.key ? "bg-[#FFBF57]/10 text-[#FFBF57] border border-[#FFBF57]/30" : "text-neutral-400 hover:text-white border border-transparent"}`} data-testid={`settings-nav-${s.key}`}>
              {s.icon} {s.label}
            </button>
          ))}
        </nav>
      </aside>
      <div className="lg:col-span-9 space-y-6">
        {section === "integraciones" && (
          <>
            <div>
              <h2 className="font-display text-2xl text-white">Integraciones</h2>
              <p className="text-sm text-neutral-400 mt-1">Conecta sistemas externos con la plataforma. Cada integración usa su propia clave de acceso.</p>
            </div>
            <RubiposApiKeyCard />
            <RubiposPullCard />
          </>
        )}
      </div>
    </div>
  );
}

export function RubiposPullCard() {
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ base_url: "", api_key: "" });
  const [ping, setPing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/rubipos/connection"); setConn(data); setForm({ base_url: data.base_url || "", api_key: "" }); setPing(data.last_ping); }
    catch (_e) { toast.error("No se pudo cargar la conexión de lectura"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/rubipos/connection", { base_url: form.base_url, api_key: form.api_key || null }); toast.success("Conexión guardada"); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post("/rubipos/connection/test"); setPing(data); data.ok ? toast.success(`Conectado · ${data.llave}`) : toast.error(data.error || "Sin conexión"); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo verificar"); }
    finally { setTesting(false); }
  };

  return (
    <div className="border border-white/10 bg-[#111112] rounded-2xl p-6" data-testid="rubipos-pull-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-400/10 border border-sky-400/30 flex items-center justify-center text-sky-300"><Plug size={16} /></div>
          <div>
            <div className="text-white font-medium">RubiPOS · API de lectura (sincronización)</div>
            <div className="text-xs text-neutral-500 mt-0.5">Permite importar sucursales, traer el historial de ventas y reconciliar cada hora de forma automática.</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono px-2.5 py-1 rounded-full border ${ping?.ok ? "border-emerald-400/40 text-emerald-300" : ping ? "border-rose-400/40 text-rose-300" : "border-white/10 text-neutral-500"}`} data-testid="rubipos-pull-status">
          <span className={`h-1.5 w-1.5 rounded-full ${ping?.ok ? "bg-emerald-400" : ping ? "bg-rose-400" : "bg-neutral-500"}`} /> {ping?.ok ? `Conectado · ${ping.llave}` : ping ? "Sin conexión" : "Sin verificar"}
        </span>
      </div>
      <form onSubmit={save} className="mt-5 grid md:grid-cols-12 gap-3">
        <label className="md:col-span-7 block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">URL base de la API</span>
          <input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://…/api/rubinova/v1" className="w-full bg-transparent border border-white/15 rounded-lg px-3 py-2.5 text-xs font-mono text-white focus:border-[#FFBF57] outline-none" data-testid="rubipos-pull-url" /></label>
        <label className="md:col-span-5 block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">API key de lectura {conn?.has_key && <span className="text-neutral-600">· actual {conn.api_key_masked}</span>}</span>
          <input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={conn?.has_key ? "Dejar vacío para conservar" : "rbk_…"} className="w-full bg-transparent border border-white/15 rounded-lg px-3 py-2.5 text-xs font-mono text-white focus:border-[#FFBF57] outline-none" data-testid="rubipos-pull-key" /></label>
        <div className="md:col-span-12 flex flex-wrap gap-2 items-center">
          <button type="submit" disabled={busy} className="bg-[#FFBF57] hover:bg-[#FFCF7F] text-black text-xs uppercase tracking-widest font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="rubipos-pull-save">{busy ? "Guardando…" : "Guardar"}</button>
          <button type="button" onClick={test} disabled={testing} className="flex items-center gap-2 px-4 py-2.5 border border-white/15 rounded-lg text-neutral-200 text-xs uppercase tracking-widest font-mono hover:border-white/40 disabled:opacity-50" data-testid="rubipos-pull-test"><Plug size={12} /> {testing ? "Verificando…" : "Verificar conexión"}</button>
          {ping && <span className="text-[11px] font-mono text-neutral-500">{ping.ok ? `alcance: ${ping.scopes?.join(", ")} · ${ping.latency_ms} ms · ${timeAgo(ping.checked_at)}` : ping.error}</span>}
        </div>
      </form>
      <div className="mt-4 text-[11px] text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
        <span>Sucursales vinculadas: <span className="text-neutral-300 font-mono">{conn?.linked_branches ?? "—"}</span></span>
        <span>Sincronización automática: <span className="text-neutral-300">cada hora</span>{conn?.last_auto_sync && <span className="text-neutral-600"> · última {timeAgo(conn.last_auto_sync.at)}</span>}</span>
      </div>
    </div>
  );
}

export function RubiposApiKeyCard({ compact = false }) {
  const [cfg, setCfg] = useState(null);
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/rubipos/settings"); setCfg(data); }
    catch (_e) { toast.error("No se pudo cargar la configuración de RubiPOS"); }
  };
  useEffect(() => { load(); }, []);

  const copy = async (text, label = "API key") => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      }
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success(`${label} copiada`);
    } catch (_e) { toast.error("No se pudo copiar. Selecciona y copia manualmente."); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rubipos/settings/regenerate");
      setConfirming(false); setShow(true);
      await load();
      await copy(data.api_key, "Nueva API key");
    } catch (_e) { toast.error("No se pudo regenerar la clave"); }
    finally { setBusy(false); }
  };

  const base = process.env.REACT_APP_BACKEND_URL;
  const masked = cfg ? `${cfg.api_key.slice(0, 5)}${"•".repeat(24)}${cfg.api_key.slice(-6)}` : "…";

  return (
    <div className="border border-white/10 bg-[#111112] rounded-2xl p-6" data-testid="rubipos-integration-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFBF57]/10 border border-[#FFBF57]/30 flex items-center justify-center text-[#FFBF57]"><KeyRound size={16} /></div>
          <div>
            <div className="text-white font-medium">RubiPOS · API key de integración</div>
            <div className="text-xs text-neutral-500 mt-0.5">Autentica los webhooks de ventas y gastos que envía el punto de venta.</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono px-2.5 py-1 rounded-full border ${cfg?.last_received_at ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-neutral-500"}`} data-testid="rubipos-integration-status">
          <span className={`h-1.5 w-1.5 rounded-full ${cfg?.last_received_at ? "bg-emerald-400" : "bg-neutral-500"}`} /> {cfg?.last_received_at ? "Conectado" : "Sin datos aún"}
        </span>
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">Clave · header <span className="text-neutral-300">X-API-Key</span></div>
        <div className="flex items-stretch gap-2 flex-wrap">
          <code onClick={() => cfg && copy(cfg.api_key)} title="Clic para copiar" className="flex-1 min-w-[260px] cursor-pointer select-all bg-black/40 border border-white/10 hover:border-[#FFBF57]/40 rounded-lg px-3 py-2.5 text-xs font-mono text-neutral-200 truncate transition-colors" data-testid="rubipos-api-key">{cfg ? (show ? cfg.api_key : masked) : "…"}</code>
          <button onClick={() => setShow(!show)} className="px-3 border border-white/10 rounded-lg text-neutral-400 hover:text-white" title={show ? "Ocultar" : "Mostrar"} data-testid="rubipos-key-toggle">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
          <button onClick={() => cfg && copy(cfg.api_key)} disabled={!cfg} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs uppercase tracking-widest font-mono font-semibold transition-colors ${copied ? "bg-emerald-400 text-black" : "bg-[#FFBF57] hover:bg-[#FFCF7F] text-black"}`} data-testid="rubipos-key-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copiada" : "Copiar"}</button>
          <button onClick={() => setConfirming(true)} className="flex items-center gap-2 px-3 py-2.5 border border-white/15 rounded-lg text-neutral-300 text-xs uppercase tracking-widest font-mono hover:border-[#FFBF57]/50 hover:text-[#FFBF57]" data-testid="rubipos-key-regenerate"><RefreshCw size={12} /> Regenerar</button>
        </div>
        <div className="text-[10px] font-mono text-neutral-600 mt-2">{cfg?.created_at ? `Generada el ${new Date(cfg.created_at).toLocaleString("es-MX")}` : ""}</div>
      </div>

      {confirming && (
        <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4" data-testid="rubipos-regenerate-confirm">
          <div className="flex items-start gap-3">
            <ShieldAlert size={16} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="text-sm text-neutral-200">
              <div className="font-medium">¿Regenerar la API key?</div>
              <p className="text-xs text-neutral-400 mt-1">La clave actual dejará de funcionar de inmediato y RubiPOS no podrá enviar ventas hasta que actualices la nueva clave en su configuración. La nueva clave se copiará automáticamente.</p>
              <div className="mt-3 flex gap-2">
                <button onClick={regenerate} disabled={busy} className="bg-amber-300 hover:bg-amber-200 text-black text-xs uppercase tracking-widest font-semibold px-4 py-2 rounded-full disabled:opacity-50" data-testid="rubipos-regenerate-confirm-btn">{busy ? "Generando…" : "Sí, regenerar"}</button>
                <button onClick={() => setConfirming(false)} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white px-3 py-2" data-testid="rubipos-regenerate-cancel">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 grid sm:grid-cols-2 gap-2 text-[11px] font-mono">
        {[["Webhook ventas", cfg && `${base}${cfg.webhook_ventas}`, "rubipos-webhook-ventas"], ["Webhook gastos", cfg && `${base}${cfg.webhook_gastos}`, "rubipos-webhook-gastos"], ["Webhook traspasos / compras", cfg && `${base}${cfg.webhook_traspasos}`, "rubipos-webhook-traspasos"], ["Webhook cortes de caja", cfg && `${base}${cfg.webhook_cortes}`, "rubipos-webhook-cortes"]].map(([l, u, tid]) => (
          <button key={l} onClick={() => u && copy(u, l)} className="text-left bg-black/30 border border-white/5 rounded-lg px-3 py-2 hover:border-white/20 group" data-testid={tid}>
            <div className="text-neutral-500 uppercase tracking-widest text-[9px] flex items-center justify-between">{l} <Copy size={10} className="opacity-0 group-hover:opacity-100" /></div>
            <div className="text-neutral-300 truncate mt-0.5">{u || "…"}</div>
          </button>
        ))}
      </div>

      {!compact && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["Última sincronización", timeAgo(cfg?.last_received_at), "rubipos-last-sync"], ["Registros recibidos", fmtNum.format(cfg?.received_count || 0)], ["Ventas almacenadas", fmtNum.format(cfg?.total_sales || 0)], ["Gastos almacenados", fmtNum.format(cfg?.total_expenses || 0)]].map(([l, v, tid]) => (
            <div key={l} className="bg-black/30 border border-white/5 rounded-lg px-3 py-2.5" data-testid={tid}>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">{l}</div>
              <div className="font-mono text-sm text-white mt-1 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setShowDocs(!showDocs)} className="mt-4 text-xs text-neutral-400 hover:text-white underline underline-offset-4" data-testid="rubipos-docs-toggle">{showDocs ? "Ocultar" : "Ver"} formato JSON esperado</button>
      {showDocs && (
        <div className="mt-3 space-y-3">
          <pre className="text-[11px] leading-relaxed font-mono text-neutral-300 bg-black/40 border border-white/5 rounded-lg p-4 overflow-x-auto whitespace-pre" data-testid="rubipos-docs">{SAMPLE_VENTA}</pre>
          <pre className="text-[11px] leading-relaxed font-mono text-neutral-300 bg-black/40 border border-white/5 rounded-lg p-4 overflow-x-auto whitespace-pre">{SAMPLE_GASTO}</pre>
          <pre className="text-[11px] leading-relaxed font-mono text-neutral-300 bg-black/40 border border-white/5 rounded-lg p-4 overflow-x-auto whitespace-pre" data-testid="rubipos-docs-traspasos">{SAMPLE_TRASPASO}</pre>
          <pre className="text-[11px] leading-relaxed font-mono text-neutral-300 bg-black/40 border border-white/5 rounded-lg p-4 overflow-x-auto whitespace-pre" data-testid="rubipos-docs-cortes">{SAMPLE_CORTE}</pre>
          <p className="text-[11px] text-neutral-500">Acepta un objeto individual o un lote (histórico). Reenviar el mismo folio actualiza la venta en lugar de duplicarla. Sucursales desconocidas se crean automáticamente en la pestaña RubiPOS.</p>
        </div>
      )}
    </div>
  );
}
